/* eslint-disable camelcase */
import { observable, action, computed } from "mobx";
import GreetingBotRepository from "~/repository/UiMeet3d/GreetingBotRepository";
import FormStore from "~/stores/FormStore";
import CustomFieldType from "~/constants/UiMeet3d/GreetingBot/CustomFieldType";
import CustomField from "~/models/UiMeet3d/GreetingBot/CustomField";
import DnDStore from "~/stores/DnDStore";

class GreetingBotStore {
  @observable greetingBot = null;

  @observable loading = false;

  @observable loaded = false;

  @observable isSaving = false;

  /**
   * Form for the bot configuration
   */
  @observable formStore = null;

  /**
   * Collection of forms that represents a custom form field
   */
  @observable customFields = [];

  /**
   * Represents the form used to add a custom form field
   */
  @observable customFieldFormStore = null;

  @observable user = null;

  // Will store a reference to the custom field in order to have a starting comparison value so we can tell
  // if the fields have been edited or not.
  @observable initialCustomFieldsHash = null;

  /**
   * @property { DnDStore } dndStore
   */
  dndStore = null;

  /** @type GreetingBotRepository */
  greetingBotRepository = null;

  /** @type UserStore */
  userStore = null;

  constructor(user, userStore) {
    // a pseudo Null Object for greetingBot. Useful to avoid null references on computed properties in this store.
    this.greetingBot = {};
    this.user = user;
    this.userStore = userStore;
    this.initializeFormStore();
    this.initializeCustomFieldFormStore({});
    this.greetingBotRepository = new GreetingBotRepository();
    this.dndStore = new DnDStore();
  }

  getCustomFieldsHash(customFields) {
    return customFields.map(c => c.hash).join("_");
  }

  @computed
  get customFieldsDirty() {
    return this.getCustomFieldsHash(this.customFields) !== this.initialCustomFieldsHash;
  }

  @action
  initializeFormStore() {
    this.formStore = new FormStore({
      enabled: false,
      name: "",
      message_enQuestion: "",
      message_enAnswerYes: "",
      message_enAnswerNo: "",
      message_frQuestion: "",
      message_frAnswerYes: "",
      message_frAnswerNo: "",
      dismiss_en: "",
      dismiss_fr: "",
      // form stuff
      action_type: "",
      action_form_title_en: "",
      action_form_title_fr: "",
    });
  }

  @action
  bindGreetingBotToForm(greetingBot) {
    const { model } = this.formStore;

    this.formStore = new FormStore({
      ...model,
      enabled: greetingBot.enabled || false,
      name: greetingBot.name || "",
      message_enQuestion: greetingBot.message.enQuestion || "",
      message_enAnswerYes: greetingBot.message.enAnswerYes || "",
      message_enAnswerNo: greetingBot.message.enAnswerNo || "",
      message_frQuestion: greetingBot.message.frQuestion || "",
      message_frAnswerYes: greetingBot.message.frAnswerYes || "",
      message_frAnswerNo: greetingBot.message.frAnswerNo || "",
      dismiss_en: greetingBot.dismiss.en || "",
      dismiss_fr: greetingBot.dismiss.fr || "",
      // form stuff
      action_type: greetingBot.action.type,
      action_form_title_en: greetingBot.action.form.title.en || "",
      action_form_title_fr: greetingBot.action.form.title.fr || "",
    });
  }

  @action
  initializeCustomFieldFormStore({
    // Note: the id never shows up in the form, but it's handy to be able to bind it to the form when in Edit mode
    // so we can track which form field to update.
    id = "",
    name = "",
    type = CustomFieldType.INPUT_TEXT,
    label_en = "",
    label_fr = "",
    value_en = "",
    value_fr = "",
    mandatory = false,
  }) {
    this.customFieldFormStore = new FormStore({
      id,
      type,
      name,
      label_en,
      label_fr,
      value_en,
      value_fr,
      mandatory,
    });
  }

  formToJS() {
    const { model } = this.formStore;

    const payload = {
      greetingBot: {
        enabled: model.enabled,
        name: model.name,
        message: {
          enQuestion: model.message_enQuestion,
          enAnswerYes: model.message_enAnswerYes,
          enAnswerNo: model.message_enAnswerNo,
          frQuestion: model.message_frQuestion,
          frAnswerYes: model.message_frAnswerYes,
          frAnswerNo: model.message_frAnswerNo,
        },
        dismiss: {
          en: model.dismiss_en,
          fr: model.dismiss_fr,
        },
        action: {
          type: model.action_type,
          form: {
            title: {
              en: model.action_form_title_en,
              fr: model.action_form_title_fr,
            },
            body: [],
          },
        },
      },
    };

    for (let i = 0; i < this.customFields.length; i += 1) {
      const customField = this.customFields[i];
      payload.greetingBot.action.form.body.push(customField.toJS());
    }

    return payload;
  }

  @computed
  get canSaveCustomField() {
    const { model, isDirty } = this.customFieldFormStore;

    if (!isDirty) {
      return false;
    }

    const customField = new CustomField({ ...model });

    return customField.isValid();
  }

  getCustomFieldFromStore() {
    const { model } = this.customFieldFormStore;

    return new CustomField({ ...model });
  }

  /**
   * @param {CustomField} field an instance of CustomField
   * @returns Boolean True if customField was added, false if not.
   */
  @action
  saveCustomField(customField) {
    if (!customField.isValid()) {
      throw new Error("Unexpected invalid customField");
    }

    // Make sure the name is not taken yet. Exit silently and show a pop up with a warning message.
    if (this.customFieldNameExists(customField)) {
      throw new Error(`customField name already taken: ${customField.nameCoalesced}`);
    }

    // check if we need to append or replace the customField (add vs edit respectively).
    const indexAt = this.customFields.findIndex(c => c.id === customField.id);

    // Does the field exists in the collection?
    if (indexAt !== -1) {
      // execute an edit.
      this.customFields.splice(indexAt, 1, customField);
    } else {
      // customField doesn't exist in the collection, let's add it.
      this.customFields.push(customField);
    }

    return true;
  }

  customFieldNameExists(customField) {
    // find all custom fields matching the same name
    const customFields = this.customFields.filter(c => c.nameCoalesced === customField.nameCoalesced);
    // filter out customField if it already exists in the collection (since it will obviously have the same name).
    const filteredCustomFields = customFields.filter(c => c.id !== customField.id);

    // if there's a customField which is not the one passed as reference, then the name is already taken.
    return filteredCustomFields.length > 0;
  }

  @action
  removeCustomField(id) {
    const indexAt = this.customFields.findIndex(f => f.id === id);
    this.customFields.splice(indexAt, 1);
  }

  @action
  receiveCustomFields(customFields) {
    this.customFields.clear();

    for (let i = 0; i < customFields.length; i += 1) {
      const data = customFields[i];
      const customField = CustomField.fromJS(data);
      this.customFields.push(customField);
    }
  }

  @action
  loadOne() {
    this.loading = true;
    return this.greetingBotRepository
      .fetchOne(this.user.userId, this.user.type)
      .then(response => this.onGreetingBotFetched(response))
      .finally(() => {
        this.loading = false;
      });
  }

  @action
  saveOneFromStore() {
    this.isSaving = true;

    const { userId, type } = this.user;

    // @todo save to customer or provider as needed.
    return this.greetingBotRepository
      .update(userId, type, this.formToJS())
      .then(response => this.onGreetingBotFetched(response))
      .finally(() => {
        this.isSaving = false;
      });
  }

  /**
   * A common handler for when we receive the greeting bot data from the backend, like when loading or after saving
   * the user's input.
   */
  @action
  onGreetingBotFetched(response) {
    this.greetingBot = response.data.greetingBot;
    this.initializeFormStore();
    this.bindGreetingBotToForm(this.greetingBot);
    this.receiveCustomFields(this.greetingBot.action.form.body);
    this.initialCustomFieldsHash = this.getCustomFieldsHash(this.customFields);
    this.loaded = true;
  }

  // DND Store delegate stuff

  @action
  reorder(sourceId, targetId) {
    this.customFields = this.dndStore.reorder(this.customFields, sourceId, targetId);
  }
}

export default GreetingBotStore;
